import {CopilotAppOpenAction} from '@github-ui/copilot-app-launcher-shared'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {buildCopilotIssueAppLink} from '@github-ui/issue-url-helper'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  ArrowRightIcon,
  CommentDiscussionIcon,
  CopyIcon,
  HeartIcon,
  LockIcon,
  PinIcon,
  PinSlashIcon,
  TrashIcon,
} from '@primer/octicons-react'
import {ActionList, CounterLabel, Heading} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useRef, useState} from 'react'

import type {OptionConfig} from '../../components/OptionConfig'
import {BUTTON_LABELS} from '../../constants/buttons'
import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {usePinIssue, useUnpinIssue} from '../../mutations/use-issue-pinning'
import {ConvertToDiscussionDialog} from '../ConvertToDiscussionDialog'
import {IssueConversationLock} from '../header/IssueConversationLock'
import {IssueDeletionConfirmationDialog} from '../header/IssueDeletionConfirmationDialog'
import {IssueTransferDialog} from '../header/IssueTransferDialog'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import {IssueFeedbackDialog, type IssueFeedbackDialogRef} from '../IssueFeedbackDialog'
import type {IssueSidebarPrimaryFragment} from '../IssueSidebar'
import styles from './OptionsSection.module.css'

const DuplicateIssueDialog = React.lazy(async () => {
  const m = await import('../DuplicateIssueDialog')
  return {default: m.DuplicateIssueDialog}
})

const PINNED_ISSUES_LIMIT = 3
const COPILOT_APP_ENTRY_POINT = 'dotcom_issue_sidebar_actions'
const optionIconViewportStyle = {
  width: 'var(--base-size-20)',
  height: 'var(--base-size-20)',
} satisfies React.CSSProperties

type OptionsSectionRepositoryNode = NonNullable<IssueViewerViewQueryQuery['repository']>
type OptionsSectionIssueNode = NonNullable<OptionsSectionRepositoryNode['issue']>
type OptionsSectionIssueRepositoryNode = NonNullable<OptionsSectionIssueNode['repository']>

export type OptionsSectionFragment = RelayCompat<
  Pick<
    OptionsSectionIssueNode,
    | 'id'
    | 'number'
    | 'title'
    | 'body'
    | 'isPinned'
    | 'locked'
    | 'viewerCanDelete'
    | 'viewerCanTransfer'
    | 'viewerCanConvertToDiscussion'
    | 'viewerCanLock'
  > & {
    readonly repository: Pick<
      OptionsSectionIssueRepositoryNode,
      'id' | 'name' | 'visibility' | 'viewerCanPinIssues' | 'isArchived'
    > & {
      readonly owner: Pick<NonNullable<OptionsSectionIssueRepositoryNode['owner']>, 'login'>
      readonly pinnedIssues: Pick<NonNullable<OptionsSectionIssueRepositoryNode['pinnedIssues']>, 'totalCount'> | null
      readonly viewerIssueCreationPermissions: Pick<
        NonNullable<OptionsSectionIssueRepositoryNode['viewerIssueCreationPermissions']>,
        'triageable'
      >
    }
  }
>

export type OptionsSectionSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

export type OptionsSectionProps = {
  optionsSectionSecondary?: OptionsSectionSecondaryFragment | null
  optionConfig: OptionConfig
  issue: OptionsSectionFragment
}

function OptionIconViewport({children}: {children: React.ReactNode}) {
  return (
    <span className="d-inline-flex flex-items-center flex-justify-center" style={optionIconViewportStyle}>
      {children}
    </span>
  )
}

export function OptionsSection({issue, optionsSectionSecondary, optionConfig}: OptionsSectionProps) {
  const [showIssueTransferDialog, setShowIssueTransferDialog] = useState(false)
  const [showConvertToDiscussionDialog, setShowConvertToDiscussionDialog] = useState(false)
  const [showIssueDeletionConfirmationDialog, setShowIssueDeletionConfirmationDialog] = useState(false)
  const [showConversationLockDialog, setShowConversationLockDialog] = useState(false)
  const [showDuplicateIssueDialog, setShowDuplicateIssueDialog] = useState(false)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const feedbackDialogRef = useRef<IssueFeedbackDialogRef>(null)

  const showFeedback = isFeatureEnabled('issues_react_ui_feedback')
  const showCopilotAppOpenAction = !isFeatureEnabled('issues_disable_copilot_app_open_action')

  const {
    id,
    number: issueNumber,
    title,
    body,
    viewerCanLock,
    viewerCanTransfer,
    viewerCanDelete,
    viewerCanConvertToDiscussion,
    isPinned,
    locked,
    repository,
  } = issue

  // If the secondary data is not available, we default to false
  const isTransferInProgress = optionsSectionSecondary?.isTransferInProgress ?? false

  const viewerCanPin = repository.viewerCanPinIssues

  const pinnedIssuesCount = repository?.pinnedIssues?.totalCount || 0

  const canPinNewIssue = pinnedIssuesCount < 3

  const {addToast} = useToastContext()

  const reportPinError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'error',
      message: ERRORS.couldNotPinIssue,
    })
  }, [addToast])

  const reportUnpinError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'error',
      message: ERRORS.couldNotUnpinIssue,
    })
  }, [addToast])

  const pinIssue = usePinIssue({
    issueId: id,
    repositoryId: repository.id,
    owner: repository.owner.login,
    repository: repository.name,
    issueNumber,
    currentPinnedCount: pinnedIssuesCount,
    onError: reportPinError,
  })
  const unpinIssue = useUnpinIssue({
    issueId: id,
    repositoryId: repository.id,
    owner: repository.owner.login,
    repository: repository.name,
    issueNumber,
    currentPinnedCount: pinnedIssuesCount,
    onError: reportUnpinError,
  })

  const pinUnpin = useCallback(() => {
    if (!viewerCanPin) return

    if (isPinned) {
      unpinIssue()
    } else if (canPinNewIssue) {
      pinIssue()
    }
  }, [canPinNewIssue, isPinned, pinIssue, unpinIssue, viewerCanPin])

  const disablePinningAction = !canPinNewIssue && !isPinned

  const allowDuplicateIssue =
    !repository.isArchived &&
    // Duplication is only shown to users with triage permission in the current repository
    repository.viewerIssueCreationPermissions.triageable

  return (
    <>
      <Heading as="h2" className="sr-only">
        {LABELS.optionsTitle}
      </Heading>
      {/* Note to maintainers: If adding support for new permissions, make sure `hideDivider` in the ParticipantsSection is updated too */}
      <ActionList variant="full" className={styles.ActionListOverrides}>
        {showCopilotAppOpenAction && (
          <CopilotAppOpenAction
            entryPoint={COPILOT_APP_ENTRY_POINT}
            open={buildCopilotIssueAppLink({owner: repository.owner.login, repo: repository.name, number: issueNumber})}
            label={BUTTON_LABELS.openInGitHubCopilotApp}
          />
        )}

        {viewerCanTransfer && (
          <ActionList.Item onSelect={() => setShowIssueTransferDialog(true)}>
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                <ArrowRightIcon />
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.transferIssue}
          </ActionList.Item>
        )}

        {allowDuplicateIssue && (
          <ActionList.Item onSelect={() => setShowDuplicateIssueDialog(true)}>
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                <CopyIcon />
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.duplicateIssue}
          </ActionList.Item>
        )}

        {viewerCanLock && (
          <ActionList.Item onSelect={() => setShowConversationLockDialog(true)}>
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                <LockIcon />
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            <span>{locked ? LABELS.lock.buttonConfirmUnlock : LABELS.lock.buttonConfirmLock}</span>
          </ActionList.Item>
        )}

        {viewerCanPin && (
          <ActionList.Item
            aria-disabled={disablePinningAction || undefined}
            onSelect={disablePinningAction ? undefined : pinUnpin}
            className={clsx(disablePinningAction && styles.DisablePin)}
            inactiveText={
              disablePinningAction
                ? `${PINNED_ISSUES_LIMIT}/${PINNED_ISSUES_LIMIT} issues already pinned. Unpin an issue to pin this one.`
                : undefined
            }
          >
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                {isPinned ? (
                  <PinSlashIcon className={clsx(disablePinningAction && styles.DisablePin)} />
                ) : (
                  <PinIcon className={clsx(disablePinningAction && styles.DisablePin)} />
                )}
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            <span>{isPinned ? BUTTON_LABELS.unpinIssue : BUTTON_LABELS.pinIssue}</span>
            {pinnedIssuesCount > 0 && (
              <ActionList.TrailingVisual>
                <CounterLabel>
                  {pinnedIssuesCount}/{PINNED_ISSUES_LIMIT}
                </CounterLabel>
              </ActionList.TrailingVisual>
            )}
          </ActionList.Item>
        )}
        {viewerCanConvertToDiscussion && (
          <ActionList.Item onSelect={() => setShowConvertToDiscussionDialog(true)}>
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                <CommentDiscussionIcon />
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.convertToDiscussion}
          </ActionList.Item>
        )}
        {viewerCanDelete && (
          <ActionList.Item variant="danger" onSelect={() => setShowIssueDeletionConfirmationDialog(true)}>
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                <TrashIcon />
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.deleteIssue}
          </ActionList.Item>
        )}
        {showFeedback && (
          <ActionList.Item
            onSelect={() => {
              if (!showFeedbackDialog) {
                setShowFeedbackDialog(true)
                feedbackDialogRef.current?.openDialog()
              }
            }}
          >
            <ActionList.LeadingVisual>
              <OptionIconViewport>
                <HeartIcon />
              </OptionIconViewport>
            </ActionList.LeadingVisual>
            Give feedback
            <IssueFeedbackDialog ref={feedbackDialogRef} onClose={() => setShowFeedbackDialog(false)} />
          </ActionList.Item>
        )}
      </ActionList>
      {showIssueTransferDialog && (
        <IssueTransferDialog
          owner={repository.owner.login}
          repository={repository.name}
          currentRepoVisibility={repository.visibility}
          issueNumber={issueNumber}
          isTransferInProgress={isTransferInProgress}
          onClose={() => setShowIssueTransferDialog(false)}
        />
      )}
      {showConvertToDiscussionDialog && (
        <Suspense fallback={null}>
          <ConvertToDiscussionDialog
            owner={repository.owner.login}
            repository={repository.name}
            number={issueNumber}
            onClose={() => setShowConvertToDiscussionDialog(false)}
          />
        </Suspense>
      )}
      {showConversationLockDialog && (
        <IssueConversationLock
          issueId={id}
          issueNumber={issueNumber}
          nameWithOwner={`${repository.owner.login}/${repository.name}`}
          isUnlocked={!locked}
          onClose={() => setShowConversationLockDialog(false)}
        />
      )}
      {showIssueDeletionConfirmationDialog && (
        <IssueDeletionConfirmationDialog
          owner={repository.owner.login}
          repository={repository.name}
          issueId={id}
          number={issueNumber}
          issueDeletionConnection={optionConfig.issueDeletionConnection}
          onSuccessfulDeletion={optionConfig.onIssueDelete}
          afterDelete={optionConfig.navigateBack}
          onClose={() => setShowIssueDeletionConfirmationDialog(false)}
        />
      )}
      {showDuplicateIssueDialog && (
        <Suspense fallback={null}>
          <DuplicateIssueDialog
            issue={issue as IssueSidebarPrimaryFragment}
            showDuplicateIssueDialog={showDuplicateIssueDialog}
            setShowDuplicateIssueDialog={setShowDuplicateIssueDialog}
            optionConfig={optionConfig}
            repositoryOwner={repository.owner.login}
            repositoryName={repository.name}
            title={title}
            body={body}
          />
        </Suspense>
      )}
    </>
  )
}
